/**
 * TXM-594 — parse do CSV de itens da Tabela NCM.
 *
 * As colunas são identificadas pelo NOME do cabeçalho, nunca pela posição (card seção 2.5.2):
 * o usuário monta a planilha dele e a ordem das colunas não é garantida.
 *
 * O Estado não vem do arquivo — é escolhido uma vez no modal e aplicado a todas as linhas.
 *
 * Função pura, sem DOM e sem HTTP, para poder ser testada isoladamente.
 */

/** "NCM/SH" -> "ncmsh", "Descrição" -> "descricao". Tolera acento, caixa e pontuação. */
function normalizarCabecalho(texto) {
  return String(texto ?? "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");
}

/** Mesmos aliases de CABECALHOS_CSV em tabela-ncm-store.js (protótipo) — mesmo arquivo CSV
 * precisa importar igual nos dois lugares. */
const COLUNAS = {
  item: ["item", "codigo", "coditem"],
  cest: ["cest"],
  ncm: ["ncmsh", "ncm"],
  descricao: ["descricao"],
  tipo: ["tipo"],
  classificacao: ["classificacao"],
  mva: ["mva", "valormva", "percentualmva"],
  // Opcionais (TXM-594 v6). Já normalizados: "aliquota_interna" e o rótulo da tela "Alíquota
  // interna (%)" viram "aliquotainterna"; o nome da coluna no banco "aliq_interna" e a
  // abreviação "Aliq. Interna" viram "aliqinterna"; "metodo_calculo" e "Método Cálculo" viram
  // "metodocalculo"; o rótulo da tela "Método de cálculo" vira "metododecalculo"; a abreviação
  // "metodo_calc" / "Método Cálc." vira "metodocalc". Cabeçalho fora da lista é ignorado e a
  // coluna inteira entra como "herda", então a lista precisa cobrir as grafias usuais.
  // O valor vai cru — quem converte vírgula, faz UPPER no método e valida é a API.
  aliq_interna: ["aliquota", "aliq", "aliquotainterna", "aliqinterna"],
  metodo_calculo: ["metodo", "metodocalculo", "metododecalculo", "metodocalc"],
};

const OBRIGATORIAS = ["item", "ncm", "descricao"];

/**
 * Divide o CSV inteiro respeitando aspas — um campo entre aspas pode conter o separador e
 * até quebra de linha, então não dá para simplesmente dar split("\n") e split(";").
 */
function dividirLinhas(texto, separador) {
  const linhas = [];
  let campos = [];
  let atual = "";
  let dentroDeAspas = false;

  for (let i = 0; i < texto.length; i++) {
    const c = texto[i];

    if (dentroDeAspas) {
      if (c === '"') {
        if (texto[i + 1] === '"') {
          atual += '"';
          i++;
        } else {
          dentroDeAspas = false;
        }
      } else {
        atual += c;
      }
      continue;
    }

    if (c === '"') {
      dentroDeAspas = true;
    } else if (c === separador) {
      campos.push(atual);
      atual = "";
    } else if (c === "\n" || c === "\r") {
      if (c === "\r" && texto[i + 1] === "\n") i++;
      campos.push(atual);
      linhas.push(campos);
      campos = [];
      atual = "";
    } else {
      atual += c;
    }
  }

  if (atual !== "" || campos.length > 0) {
    campos.push(atual);
    linhas.push(campos);
  }

  return linhas;
}

/** Card aceita ";" ou ",": vence o que mais aparece na primeira linha. */
function detectarSeparador(texto) {
  const primeiraLinha = texto.split(/\r?\n/, 1)[0] || "";
  const pontoEVirgula = (primeiraLinha.match(/;/g) || []).length;
  const virgula = (primeiraLinha.match(/,/g) || []).length;
  return pontoEVirgula >= virgula ? ";" : ",";
}

/**
 * @param {string} texto conteúdo bruto do arquivo
 * @returns {{ itens: Array<object>, erroArquivo: string|null, totalLinhas: number }}
 */
export function parseCsvNcm(texto) {
  // BOM do Excel vira parte do nome da primeira coluna se não for removido.
  const conteudo = String(texto ?? "").replace(/^﻿/, "").trim();

  if (!conteudo) {
    return { itens: [], erroArquivo: "Arquivo vazio.", totalLinhas: 0 };
  }

  const linhas = dividirLinhas(conteudo, detectarSeparador(conteudo));
  if (linhas.length < 2) {
    return {
      itens: [],
      erroArquivo: "Arquivo sem linhas de dados (só o cabeçalho foi encontrado).",
      totalLinhas: 0,
    };
  }

  const cabecalho = linhas[0].map(normalizarCabecalho);
  const indicePorCampo = {};
  Object.entries(COLUNAS).forEach(([campo, aliases]) => {
    const indice = cabecalho.findIndex((c) => aliases.includes(c));
    if (indice >= 0) indicePorCampo[campo] = indice;
  });

  const faltando = OBRIGATORIAS.filter((campo) => indicePorCampo[campo] === undefined);
  if (faltando.length > 0) {
    const rotulos = { item: "Item", ncm: "NCM/SH", descricao: "Descrição" };
    return {
      itens: [],
      erroArquivo: `Coluna(s) obrigatória(s) não encontrada(s) no cabeçalho: ${faltando
        .map((c) => rotulos[c])
        .join(", ")}.`,
      totalLinhas: 0,
    };
  }

  const valor = (linha, campo) => {
    const indice = indicePorCampo[campo];
    if (indice === undefined) return null;
    const bruto = (linha[indice] ?? "").trim();
    return bruto === "" ? null : bruto;
  };

  const dados = linhas.slice(1).filter((linha) => linha.some((c) => (c ?? "").trim() !== ""));

  // Linhas incompletas seguem para o backend, que é quem decide e contabiliza o descarte —
  // assim o resultado é o mesmo vindo do CSV ou de qualquer outro caminho de inclusão.
  const itens = dados.map((linha) => ({
    item: valor(linha, "item"),
    cest: valor(linha, "cest"),
    ncm: valor(linha, "ncm"),
    descricao: valor(linha, "descricao"),
    tipo: valor(linha, "tipo"),
    classificacao: valor(linha, "classificacao"),
    mva: valor(linha, "mva"),
    aliq_interna: valor(linha, "aliq_interna"),
    metodo_calculo: valor(linha, "metodo_calculo"),
  }));

  return { itens, erroArquivo: null, totalLinhas: itens.length };
}
